import { WixSite } from '@wix/media-manager-sdk';
import {
  all,
  allPass,
  always,
  any,
  anyPass,
  both,
  complement,
  contains,
  curry,
  defaultTo,
  equals,
  filter,
  find,
  head,
  ifElse,
  indexBy,
  indexOf,
  is,
  isNil,
  keys,
  last,
  match,
  not,
  nth,
  path,
  pipe,
  prop,
  propEq,
  propOr,
  propSatisfies,
  sortBy,
  startsWith,
  trim,
  values,
} from 'ramda';
import { contained, ensureArray, hasPath, isArray, isFalsy, isTruthy } from 'ramda-adjunct';
import { IntlFormatters } from 'react-intl';
import { ItemType } from '../../constants/ItemType';
import { RootId } from '../../constants/MediaFromWixCategory';
import { MediaType } from '../../constants/MediaType';
import { Pricing } from '../../constants/Pricing';
import { ExternalSource, MainSource, Source } from '../../constants/Source';
import { SYSTEM_BOARDS_TRANSLATIONS } from '../../constants/SystemBoardId';
import { SystemFolderId } from '../../constants/SystemFolderId';
import { TagPrefix } from '../../constants/SystemTags';
import { Tags } from '../../constants/Tags';
import { TranscodingStatus } from '../../constants/TranscodingStatus';
import { getId } from '../common';
import { MediaProducer } from '../hoc/bi/withPurchaseAnalytics/withPurchaseAnalytics.types';
import { JumpTo } from '../hoc/withItemsReducer/withItemsReducer.types';
import { MediaManagerSDKPublicMediaFolder } from '../hoc/withPublicMedia/withPublicMedia.types';
import { memoize } from '../memoize';
import { convertMSSToMMSS } from '../time';
import { getExtension } from '../path';
import { isBoard, isSystemBoard } from './boards';
import { NEW_FOLDER_ID_PREFIX } from './constants';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKBoard,
  MediaManagerSDKFile,
  MediaManagerSDKFileOutputImage,
  MediaManagerSDKFileOutputVideo,
  MediaManagerSDKFolder,
  MediaManagerSDKTeam,
  MediaManagerSDKThreeDPreview,
} from './index';
import { isMediaFromWix, isShutterstock, isUnsplash } from './sources';
import { isFailed, isTranscoding } from './transcoding';

type TFolderChecker = (folder: MediaManagerSDKFolder | MediaManagerSDKBoard) => boolean;
type TFileChecker = (file: MediaManagerSDKFile) => boolean;
type TItemChecker = (item: MediaManagerSDKAnyItem) => boolean;
type TItemsChecker = (items: MediaManagerSDKAnyItem[]) => boolean;

const PICTURE_TYPES = [MediaType.Picture, MediaType.SecurePicture, MediaType.SiteIcon, MediaType.VectorArt];

export const getItemById: (itemId: string, items: MediaManagerSDKAnyItem[]) => MediaManagerSDKAnyItem = (
  itemId,
  items,
) => find<MediaManagerSDKAnyItem>(propEq('id', itemId), items);

export const validateFileName = trim;

export const isNativeFile = is(File);
export const areNativeFiles = all(isNativeFile);

// ADI Recommended is not passing "kind" property to an item
export const isFile = (item: MediaManagerSDKAnyItem): item is MediaManagerSDKFile =>
  item && (item.kind === ItemType.File || !item.kind);

export const isFolder = (item: MediaManagerSDKAnyItem | string): item is MediaManagerSDKFolder =>
  item && (item as MediaManagerSDKAnyItem).kind === ItemType.Folder;

export const areMediaManagerSDKItems = (items: MediaManagerSDKAnyItem[]) => all(anyPass([isFile, isFolder]), items);

export const getItemSource = prop('source');

export const hasUser: TItemChecker = (item) => isTruthy(item.userId);

const createContainsTagFunction = (tagsPropName: string) =>
  curry<string, any, boolean>((tag: string, item: any): boolean => pipe(propOr([], tagsPropName), contains(tag))(item));

export const containsTag = createContainsTagFunction('tags');
export const containsSystemTag = createContainsTagFunction('systemTags');

export const isItemFavorite: TItemChecker = (item) => containsTag(TagPrefix.Favorites, item);

export const isItemFromVideoMaker: TItemChecker = (item) => containsTag(TagPrefix.VideoMaker, item);

export const isItemFromVimeoCreator: TItemChecker = containsTag(TagPrefix.VimeoCreator);

export const isPurchasedFromShutterstock: TItemChecker = allPass([
  containsTag(TagPrefix.ShutterStock),
  containsTag(TagPrefix.Paid),
]);

export const canEditItemInVideoMaker = (files: MediaManagerSDKAnyItem | MediaManagerSDKAnyItem[]) => {
  const filesArray = ensureArray<MediaManagerSDKAnyItem>(files);

  if (any(isFolder, filesArray)) {
    return false;
  }

  return filesArray.length === 1 && isItemFromVideoMaker(head(filesArray));
};

export const isRootFolder: TFolderChecker = both(
  isFolder,
  propSatisfies<string, MediaManagerSDKFolder>(isNil, 'parentId'),
);

export const isMobileFolder: TFolderChecker = both(isFolder, propEq('id', SystemFolderId.Mobile));

export const isPurchasedFolder: TFolderChecker = both(isFolder, propEq('id', SystemFolderId.Purchased));

export const isWixVideoLibraryLink: TFolderChecker = propEq('id', SystemFolderId.WixVideoLink);

export const isWixVideoLibrary: TFolderChecker = (folder) => containsTag('_wix_video_library', folder);

export const isMyUploadsFolder: TFolderChecker = both(isFolder, propEq('id', SystemFolderId.AllWixMedia));

export const isAllMyFilesFolder: TFolderChecker = (folder) => containsTag('_all-my-files-folder', folder);

export const filesInThisFolder = (folder: MediaManagerSDKFolder): TFileChecker => propEq('folderId', folder.id);

export const foldersInThisFolder = (folder: MediaManagerSDKFolder): TFolderChecker => propEq('parentId', folder.id);

export const getItemType: (item: MediaManagerSDKAnyItem) => ItemType = (item) =>
  isBoard(item) ? ItemType.Board : isFile(item) ? ItemType.File : ItemType.Folder;

export const getFileMediaType = prop('mediaType');

export const isSpecificFileType = (types: MediaType[]) =>
  both(isFile, pipe<MediaManagerSDKFile, string, boolean>(getFileMediaType, contained(types)));

export const hasSourcePublicMediaTag: TItemChecker = (item) => containsTag(Tags.SourcePublicMedia, item);

export const getFileExtensionFromURL = (url: string): string => {
  const filename = url.split('/').pop().split(/#|\?/)[0];

  if (!filename || filename.split('.').length < 2) {
    return '';
  }

  return filename.split('.').pop().toLowerCase();
};

export const getFileExtension = (file: MediaManagerSDKFile): string => {
  const extensionFromFileInput = path<string>(['fileInput', 'format'], file);
  if (extensionFromFileInput) {
    return extensionFromFileInput;
  }
  if (file.mediaType === MediaType.Video) {
    if (file.mimeType) {
      return file.mimeType.split('/').pop();
    }
    const format = path<string>(['fileOutput', 'video', '0', 'format'], file);
    if (format) {
      return format;
    }
    const videoPreviewUrl = path<string>(['videoPreviewUrls', '0'], file);
    if (!videoPreviewUrl) {
      const originalFileName = path<string>(['original_file_name'], file);
      return originalFileName ? getFileExtensionFromURL(originalFileName) : '';
    }

    return getFileExtensionFromURL(videoPreviewUrl);
  }

  if (isUnsplash(file.source)) {
    return pipe(prop('fileUrl'), match(/&fm=([^&]*)/), defaultTo(['', '']), nth(1))(file);
  }

  const urlToParse = file.fileUrl || file.thumbnailUrl;

  return urlToParse ? getFileExtensionFromURL(urlToParse) : '';
};

export const isPNGPictureFile: TFileChecker = pipe(getFileExtension, equals('png'));

export const isPictureFile: TFileChecker = isSpecificFileType(PICTURE_TYPES);
export const isSimplePictureFile: TFileChecker = isSpecificFileType([MediaType.Picture, MediaType.SecurePicture]);
export const isSWFFile: TFileChecker = isSpecificFileType([MediaType.SWF]);
export const isVectorArtFile: TFileChecker = isSpecificFileType([MediaType.VectorArt]);

export const isSpecificType = contained;

export const isPictureType = isSpecificType(PICTURE_TYPES);

export const isMusicFile: TFileChecker = isSpecificFileType([MediaType.Music, MediaType.SecureMusic]);

export const isSiteIconFile: TFileChecker = isSpecificFileType([MediaType.SiteIcon]);
export const is3dFile: TFileChecker = isSpecificFileType([MediaType.ThreeD]);

export const isVideoFile: TFileChecker = isSpecificFileType([MediaType.Video, MediaType.SecureVideo]);

export const isTransparentVideoFile: TFileChecker = (file) => containsTag('_mp4_alpha', file);

export const isDocumentFile: TFileChecker = isSpecificFileType([MediaType.Document, MediaType.SecureDocument]);

export const isZipFile: TFileChecker = isSpecificFileType([MediaType.Zip, MediaType.SecureZip]);

export const isSecureFile: TFileChecker = isSpecificFileType([
  MediaType.SecurePicture,
  MediaType.SecureMusic,
  MediaType.SecureVideo,
  MediaType.SecureDocument,
  MediaType.SecureZip,
]);

export const SMART_GRID_THUMB_MIN_WIDTH = 150;
export const SMART_GRID_THUMB_MAX_WIDTH = 400;
export const SMART_GRID_ROW_HEIGHT = 174;
export const SMART_GRID_SMALL_ROW_HEIGHT = 150;
export const SMART_GRID_SMALL_ROW_WINDOW_HEIGHT_THRESHOLD = 800;

export const MAX_SHUTTERSTOCK_THUMB_SIZE_WITHOUT_WATERMARK = 250;

export const MAX_WIX_PAID_THUMB_SIZE_WITHOUT_WATERMARK = 350;

export const UNSPLASH_THUMBNAIL_MAX_WIDTH = 350;

const isItemSourceOneOf = (sources: Source[]) => pipe(getItemSource, contained(sources));

export const isInTrash: TItemChecker = propOr(false, 'isInTrash');

export const isSiteFilesItem = propEq('source', MainSource.SiteFiles);
export const isMediaFromWixItem = propEq('source', MainSource.MediaFromWix);
export const isShutterstockItem = propEq('source', MainSource.Shutterstock);
export const isUnsplashItem = propEq('source', MainSource.Unsplash);
export const isVisitorUploadsItem = propEq('source', MainSource.VisitorsUploads);

export const isMediaFromWixFolder = allPass([isMediaFromWixItem, isFolder]);

export const isPublicOrShutterstockVideoFile: TFileChecker = allPass([
  isVideoFile,
  anyPass([isMediaFromWixItem, isShutterstockItem]),
]);

export const getPictureUrl: (file: MediaManagerSDKFile, width: number, height: number) => string = memoize(
  (file: MediaManagerSDKFile, width: number, height: number): string => {
    if (isTranscoding(file) || isFailed(file)) {
      return null;
    }

    const shouldShowShutterstockItemWithWatermark =
      isShutterstockItem(file) &&
      (width > MAX_SHUTTERSTOCK_THUMB_SIZE_WITHOUT_WATERMARK || height > MAX_SHUTTERSTOCK_THUMB_SIZE_WITHOUT_WATERMARK);

    const shouldWixPaidItemWithWatermark =
      isMediaFromWixItem(file) &&
      (width > MAX_WIX_PAID_THUMB_SIZE_WITHOUT_WATERMARK || height > MAX_WIX_PAID_THUMB_SIZE_WITHOUT_WATERMARK);

    if (shouldShowShutterstockItemWithWatermark || shouldWixPaidItemWithWatermark) {
      return file.previewUrl || file.fileUrl;
    }

    if (is3dFile(file) || isDocumentFile(file)) {
      const thumbnailInfo = path(['fileOutput', 'preview', '0'], file) as MediaManagerSDKThreeDPreview;
      if (thumbnailInfo && thumbnailInfo.status === TranscodingStatus.Ready) {
        return thumbnailInfo.url;
      }
    }

    if (isUnsplashItem(file)) {
      return (width > UNSPLASH_THUMBNAIL_MAX_WIDTH ? file.previewUrl : file.thumbnailUrl) || file.fileUrl;
    }

    return file.thumbnailUrl || file.fileUrl;
  },
);

export const isSystemFolder: TFolderChecker = anyPass([
  pipe(getId, contained(values(SystemFolderId))),
  containsTag('_system'),
]);

export const isJustCreatedFolderId = pipe<string, string, boolean>(defaultTo(''), startsWith(NEW_FOLDER_ID_PREFIX));

export const isNotJustCreatedFolderId = complement(isJustCreatedFolderId);

export const isNotJustCreatedFolder = pipe(getId, isNotJustCreatedFolderId);

export const isFileExpandable: TFileChecker = anyPass([
  isPictureFile,
  isMusicFile,
  isVideoFile,
  isTransparentVideoFile,
  isDocumentFile,
  isSWFFile,
  isZipFile,
  is3dFile,
]);

export const isFileExpandableFromActions: TFileChecker = anyPass([
  isPictureFile,
  isVideoFile,
  isTransparentVideoFile,
  isSWFFile,
]);

export const canItemBeImportedByImportFn: TItemChecker = (item) => item.source === ExternalSource.WixVideo;

export const canItemBeImportedByCopying: TItemChecker = (item) => item.source === ExternalSource.WixSite;

export const canItemBeBought: TItemChecker = (item) =>
  isFile(item) &&
  (isShutterstock(item.source) || (isMediaFromWix(item.source) && containsSystemTag(Pricing.Paid, item)));

export const canItemBeShared = (item, team) => !(team && isPurchasedFromShutterstock(item));

export const canItemsBeShared = (items, team: MediaManagerSDKTeam) =>
  items.every((item) => canItemBeShared(item, team));

export const canItemBeSubmitted: TItemChecker = (item) =>
  isFile(item) &&
  contains(item.source, [
    MainSource.SiteFiles,
    MainSource.VisitorsUploads,
    MainSource.MediaFromWix,
    MainSource.Recommended,
  ]);

export const canItemBeExpanded: TItemChecker = anyPass([both(isFile, isFileExpandable), isFolder]);
export const canItemBeExpandedFromActions: TItemChecker = anyPass([
  both(isFile, isFileExpandableFromActions),
  isFolder,
]);

export const canItemsBeExpanded: TItemsChecker = all(canItemBeExpanded);

export const hasLabels: TItemChecker = pipe(propOr(null, 'labels'), isArray);

export const hasSize: TItemChecker = (item) => isFile(item) && !isNil(item.fileSize);

export const hasResolution: TItemChecker = (item) => isFile(item) && !isNil(item.width) && !isNil(item.height);

export const hasDuration: TItemChecker = (item) => isFile(item) && hasPath(['fileInput', 'duration'], item);

export const isItemRenamable: TItemChecker = (item) => {
  if (isTranscoding(item)) {
    return false;
  }

  if (isFolder(item) && (isSystemFolder(item) || isWixVideoLibraryLink(item))) {
    return false;
  }

  if (isBoard(item)) {
    return false;
  }

  return allPass([
    isItemSourceOneOf([MainSource.SiteFiles, MainSource.Favorites, MainSource.VisitorsUploads]),
    isNotJustCreatedFolder,
  ])(item);
};

export const canOpenItemInImageEditor: TItemChecker = (item) => {
  if (isFolder(item) || isBoard(item)) {
    return false;
  }

  return (
    contains(item.source, [MainSource.SiteFiles, MainSource.Favorites, MainSource.Unsplash]) &&
    contains(item.mediaType, [MediaType.Picture, MediaType.Watermark])
  );
};

export const canOpenItemInAdobeExpress =
  (isHidden: boolean): TItemChecker =>
  (item): boolean =>
    !isHidden &&
    canOpenItemInImageEditor(item) &&
    isFile(item) &&
    // unsplash is always jpg or jpeg
    (item.source === MainSource.Unsplash || ['jpg', 'jpeg', 'png'].includes(getExtension(item.originalUrl || '')));

export const canUseItemInVideoMaker: TItemChecker = (item) => {
  if (isFolder(item) || isTranscoding(item) || isBoard(item)) {
    return false;
  }

  return allPass([
    isSpecificFileType([MediaType.Picture, MediaType.Video]),
    isItemSourceOneOf([MainSource.SiteFiles, MainSource.Favorites]),
  ])(item);
};

export const isItemMovable: TItemChecker = (item) => {
  if (isTranscoding(item)) {
    return false;
  }

  return allPass([isNotJustCreatedFolder, isItemSourceOneOf([MainSource.SiteFiles, MainSource.Favorites])])(item);
};

const day = 86400000;
export const isItemBeingTranscodedForTooLong: TItemChecker = (item, time = day) =>
  Date.now() - item.createdAt * 1000 >= time;

export const isItemRemovable: TItemChecker = (item) => {
  if (isTranscoding(item)) {
    return isItemBeingTranscodedForTooLong(item);
  }

  if (isFolder(item) && isSystemFolder(item)) {
    return false;
  }

  return allPass([
    isNotJustCreatedFolder,
    isItemSourceOneOf([MainSource.SiteFiles, MainSource.Favorites, MainSource.VisitorsUploads]),
  ])(item);
};

export const isItemCopyable: TItemChecker = (item) => {
  if (isTranscoding(item)) {
    return false;
  }

  if (isFolder(item) && isSystemFolder(item)) {
    return false;
  }

  return allPass([isNotJustCreatedFolder, isItemSourceOneOf([MainSource.SiteFiles, MainSource.Favorites])])(item);
};

const SOURCES_CAN_BE_ADDED_TO_FAVORITES = [
  MainSource.SiteFiles,
  MainSource.VisitorsUploads,
  MainSource.Shutterstock,
  MainSource.Unsplash,
  MainSource.MediaFromWix,
];

export const canItemBeAddedToFavorite: TItemChecker = (item) => {
  if (!item) {
    return false;
  }

  if (isItemFavorite(item)) {
    return false;
  }
  if (item.id === SystemFolderId.WixVideoLink) {
    return false;
  }

  return allPass([isItemSourceOneOf(SOURCES_CAN_BE_ADDED_TO_FAVORITES), isNotJustCreatedFolder])(item);
};

export const canItemBeRemovedFromFavorite: TItemChecker = (item) => {
  if (!isItemFavorite(item)) {
    return false;
  }

  return isItemSourceOneOf(SOURCES_CAN_BE_ADDED_TO_FAVORITES)(item);
};

export const isItemCuttable: TItemChecker = (item) => {
  if (isTranscoding(item)) {
    return false;
  }

  if (isFolder(item) && isSystemFolder(item)) {
    return false;
  }

  return allPass([isItemSourceOneOf([MainSource.SiteFiles, MainSource.Favorites]), isNotJustCreatedFolder])(item);
};

export const isWixSiteFolder = (folder) =>
  folder.source === ExternalSource.WixSite && folder.parentId === SystemFolderId.WixSites;

export const isItemImportable: TItemChecker = (item) => {
  if (isBoard(item)) {
    return false;
  }

  if (isFile(item)) {
    return true;
  }

  return item.source === ExternalSource.WixSite && !isWixSiteFolder(item) && !isSystemFolder(item);
};

const canItemBeUsedOutsideMMGR: TItemChecker = (item) => {
  if (isFolder(item)) {
    return false;
  }

  if (isBoard(item)) {
    return;
  }

  if (isTranscoding(item)) {
    return false;
  }

  if (hasSourcePublicMediaTag(item)) {
    return false;
  }

  if (isPurchasedFromShutterstock(item)) {
    return false;
  }

  return contains(item.source, [MainSource.SiteFiles, MainSource.Favorites, MainSource.VisitorsUploads]);
};

export const canCopyLinkBeProvided: TItemChecker = allPass([canItemBeUsedOutsideMMGR, pipe(isSecureFile, not)]);

export const isItemDownloadable: TItemChecker = canItemBeUsedOutsideMMGR;

export const canPasteToItem: TItemChecker = (item) => {
  if (isFile(item)) {
    return false;
  }

  return allPass([isItemSourceOneOf([MainSource.SiteFiles]), isNotJustCreatedFolder])(item);
};

export const canCreateNewSubfolder: TItemChecker = (item) => {
  if (!item) {
    return false;
  }

  if (isFile(item)) {
    return false;
  }

  return contains(item.source, [MainSource.SiteFiles]);
};

export const getVideoThumbnail = (item: MediaManagerSDKFile): string => {
  const thirdThumbnail = path<string>(['fileOutput', 'image', 2, 'url'], item);
  if (thirdThumbnail) {
    return thirdThumbnail;
  }

  return (
    pipe<
      MediaManagerSDKFile,
      MediaManagerSDKFileOutputImage[],
      MediaManagerSDKFileOutputImage[],
      MediaManagerSDKFileOutputImage,
      string
    >(
      path(['fileOutput', 'image']),
      defaultTo([{ url: item.thumbnailUrl }]),
      find(pipe(prop('url'), isTruthy)),
      prop('url'),
    )(item) || ''
  );
};

export const getDurationInMMSS = pipe<MediaManagerSDKFile, number, number, string>(
  path(['fileInput', 'duration']),
  defaultTo(0),
  ifElse(Boolean, convertMSSToMMSS, always(null)),
);

type TItemTypeChecker = (item: MediaManagerSDKAnyItem) => boolean;

type TItemTypeCheckerResult = (props: { item: MediaManagerSDKAnyItem }) => boolean;

export const checkPropsItemForType: (checker: TItemTypeChecker) => TItemTypeCheckerResult =
  (checker) =>
  ({ item }) =>
    checker(item);

export const checkPropsItemForTypes: (checkers: TItemTypeChecker[]) => TItemTypeCheckerResult =
  (checkers) =>
  ({ item }) =>
    anyPass(checkers)(item);

export const selectExpandedItemToJump = ({
  activeItem,
  items,
  jumpTo,
}: {
  activeItem: MediaManagerSDKAnyItem;
  items: MediaManagerSDKAnyItem[];
  jumpTo: JumpTo;
}): MediaManagerSDKAnyItem => {
  const index = indexOf(activeItem, items);
  const newPossibleItem = ifElse(
    equals(JumpTo.Prev),
    () => (index !== 0 ? items[index - 1] : null),
    () => (index !== items.length - 1 ? items[index + 1] : null),
  )(jumpTo);

  return newPossibleItem || activeItem;
};

// TODO: fix Public Media CMS https://jira.wixpress.com/browse/MMGR-2415
const INVALID_CREATOR_PICTURE = 'media/0';
export const canDisplayCreatorPictureAndName = (item) =>
  Boolean(item.creatorPicture && item.creatorPicture !== INVALID_CREATOR_PICTURE && item.creator);

export const convertBitrate = (bitrate) => Math.round(bitrate / 1000);

export const sameBy = curry<any, MediaManagerSDKAnyItem[], boolean>((fn, data) =>
  pipe(indexBy(fn), keys, propEq('length', 1))(data),
);

export const getTranslatedItemName = memoize(
  (item: MediaManagerSDKAnyItem, formatMessage: IntlFormatters['formatMessage']) => {
    if (isBoard(item)) {
      return isSystemBoard(item) ? formatMessage({ id: SYSTEM_BOARDS_TRANSLATIONS[item.id] }) : item.name;
    }

    if (isFile(item)) {
      return isUnsplashItem(item)
        ? formatMessage({ id: 'unsplash.image-by-creator' }, { creator: item.creator })
        : item.name;
    }

    if (isWixVideoLibraryLink(item)) {
      return formatMessage({ id: 'system-folder.video_library_name' });
    }

    if (isMediaFromWixFolder(item)) {
      return formatMessage({ id: 'categories.free-from-wix' });
    }

    if (isMobileFolder(item)) {
      return formatMessage({ id: 'system-folder.mobile' });
    }

    if (isMobileFolder(item)) {
      return formatMessage({ id: 'system-folder.mobile' });
    }

    if (isPurchasedFolder(item)) {
      return formatMessage({ id: 'system-folder.purchased' });
    }

    if (isAllMyFilesFolder(item)) {
      return formatMessage({ id: 'system-folder.all-my-files' });
    }

    return item.name;
  },
);

export const getCopyableFileUrl = (item: MediaManagerSDKFile, videoFilesUrlDomain: string): string => {
  if (!isSiteFilesItem(item) && !isVisitorUploadsItem(item)) {
    return null;
  }

  if (!isVideoFile(item)) {
    return item.originalUrl;
  }

  const fileUrl = pipe<
    MediaManagerSDKFile,
    MediaManagerSDKFileOutputVideo[],
    MediaManagerSDKFileOutputVideo[],
    MediaManagerSDKFileOutputVideo[],
    MediaManagerSDKFileOutputVideo[],
    MediaManagerSDKFileOutputVideo,
    string
  >(
    path(['fileOutput', 'video']),
    defaultTo([]),
    filter(propEq('status', TranscodingStatus.Ready)),
    sortBy(pipe(prop('quality'), (quality) => parseInt(quality, 10))),
    last,
    prop('url'),
  )(item);

  if (!fileUrl) {
    return null;
  }

  return `${videoFilesUrlDomain}/${fileUrl}`;
};

export const canFolderWithIdBeNavigated = anyPass([isFalsy, isNotJustCreatedFolderId]);

export const isSiteFilesRootFolderId = equals<string>(SystemFolderId.SiteFiles);

export type TDecodedWixSiteFileFolderId = {
  id: string | null;
  siteMediaToken: string | null;
};

export const decodeWixSiteFolderId = (folderId: string): TDecodedWixSiteFileFolderId => {
  try {
    return JSON.parse(folderId);
  } catch {}
};

export const isAllWixMediaRootFolder = equals(WixSite.oldMediaManagerFolder.id);

export const getWixSiteMediaTokenFromFolderId = (folderId: string): string => {
  const decodedFolderId = decodeWixSiteFolderId(folderId);

  return decodedFolderId && decodedFolderId.siteMediaToken;
};

export const getMediaManagerSDKFileExtension = (item: MediaManagerSDKFile): string => {
  // TODO: implement extension receiving for WixStock when it be ready
  return isShutterstockItem(item) ? (isPictureFile(item) ? 'jpg' : 'mp4') : '';
};

export const isBlob = is(Blob);

export const getMediaProducer = (item: MediaManagerSDKFile): MediaProducer =>
  isShutterstockItem(item) ? MediaProducer.ShutterStock : MediaProducer.WixStock;

export const isDividersCategory = (publicMediaCurrentCategory: MediaManagerSDKPublicMediaFolder) =>
  publicMediaCurrentCategory?.id === RootId.Dividers;
